// 代管中台（fleet）API —— 對應後端 `app/api/admin_hosting.py`（同一個 `/api/admin` prefix）。
//
// ⚠️ 命名刻意與 `ls.ts` 的 `Host`／`listCustomerHosts` 區隔：那組是**授權主機**（一客戶一台機器
// 一張授權），這裡的 `ManagedHost` 是**我方代管的 VM**，底下可跑多套 CS（`HostSite`）。
// 函式名一律帶 managed 前綴防閱讀混淆。
//
// 角色 gate（後端 fail-closed）：讀取＋ack＝operator 可；建立／修改／註冊碼＝admin 以上。

import { api, qs } from './client'
import type { ApproveHostedInput, TrialPreset, UnknownModulesWarning } from './ls'
import { useAuthStore } from '@/stores/auth'

/**
 * 這支 SPA 的代管 API **前綴**（批99-B）——我方員工打 `${base()}/...`，
 * 代理商打 `${base()}/reseller/...`。
 *
 * ★★ 為什麼是一支函式而不是常數：角色來自 `GET /me`（登入後才知道），而本檔在
 *   模組載入時就被 import——寫成模組級常數會永遠取到「還沒載入角色」那一刻的值。
 *   每次呼叫才問 store，代理商登入後第一個請求就打對前綴。
 *
 * ★ 兩個 realm 的**路徑與回應形狀逐支相同**（後端 `admin_reseller_hosting.py` 比照
 *   `admin_hosting.py` 複製語意、共用 service 層），所以這裡只換前綴、**函式簽章一律不動**
 *   ——六個代管 view 因此可以原封重用，不必複製 1857 行的 `HostDetailView`。
 *   唯二例外是 `issueHostSiteSsoTicket`／`listHostSiteSsoGrants`：代理商 realm **沒有**
 *   這兩支端點（以供應商身分登入是我方專屬的支援權力），view 端以 `auth.isReseller`
 *   整塊不渲染；真的打過去會是 404。
 *
 * 釘住這件事的是 `views/HostDetailView.spec.ts`：代理商視角掛載後，所有 fetch 的 URL
 * 都必須以 `${base()}/reseller/` 開頭（漏改一支就紅）。
 */
function base(): string {
  return useAuthStore().isReseller ? '/api/admin/reseller' : '/api/admin'
}

/** 主機生命週期（`managed_hosts.status`）。 */
export type HostStatus = 'pending' | 'active' | 'draining' | 'retired'
/** 告警級別；排序與色階都照這個順序（crit → warn → info）。 */
export type AlertSeverity = 'crit' | 'warn' | 'info'

/** 未解決告警計數（後端已排除被父告警抑制的，否則一台 VM 掛掉數字會爆）。 */
export interface AlertCounts {
  crit: number
  warn: number
  info: number
}

/**
 * 單一掛載點水位。⚠️ agent（`ipo-agent/`）尚未實作 → 鍵名以 LS 端目前的測試 payload 為準
 * （`{path, used_mb, total_mb, used_pct}`），**讀取一律容錯**：路徑走 `mountPath()`，
 * 容量走 `lib/fleet.ts` 的 `fmtCapacity()`（MB 與 bytes 兩種都吃）。
 */
export interface HostMount {
  path?: string | null
  mount?: string | null
  mountpoint?: string | null
  device?: string | null
  used_pct?: number | null
  used_mb?: number | null
  total_mb?: number | null
  used_bytes?: number | null
  total_bytes?: number | null
  [key: string]: unknown
}

/** agent 心跳的完整指標快照（自由形狀 JSONB；派生欄 cpu/mem/disk 已另存欄位）。 */
export interface HostMetrics {
  cpu_pct?: number | null
  /** [load1, load5, load15]。 */
  load?: number[] | null
  mem?: {
    used_pct?: number | null
    used_mb?: number | null
    total_mb?: number | null
    used_bytes?: number | null
    total_bytes?: number | null
  } | null
  mounts?: HostMount[] | null
  containers?: number | null
  [key: string]: unknown
}

/**
 * 一筆 docker image（agent 0.2.8+ 全量盤點；`managed_hosts.images`）。
 *
 * ⚠️ 形狀權威在 `license-server/docs/AGENT_API.md`——LS 原封轉發、不解析。
 * - `repository`／`tag` 為 null＝**dangling image**（docker 印 `<none>`）。刻意照收不過濾：
 *   那正是「這台磁碟為什麼滿了」最常見的答案。
 * - `size` 是 docker 的**人讀字串**（`"1.34GB"`），UI 直接印、不要自己算；
 *   `size_bytes` 是 agent 的 best-effort 解析，**只用來排序**（解析不出來是 null → 排最後）。
 * - `id` 是 docker 短 id（12 hex）。
 */
export interface HostImage {
  id: string
  repository: string | null
  tag: string | null
  size: string | null
  size_bytes: number | null
  created_at: string | null
}

/**
 * 一站的一個容器（agent 0.2.8+；`host_sites.containers`）。
 *
 * `container_id` 是**完整 64 hex**（現場排查要能複製去貼 `docker logs <id>`）——
 * UI 只顯示前 12 碼、完整值放 `title`。`image` 為 null＝舊 docker 吐不出
 * `.Config.Image`（＝不知道，不是沒有）。
 */
export interface SiteContainer {
  name: string
  container_id: string
  service: string
  /** running／exited／restarting／created…（docker `.State.Status` 原值）。 */
  status: string
  image: string | null
  restarts: number
}

/** 每日水位（最近 14 天，取當日最大值）——磁碟線性外推的資料來源。 */
export interface HostDailyMetric {
  d: string
  disk_pct?: number | null
  mem_pct?: number | null
}

/** 清單列（`GET /hosts`；後端已排序＝crit → warn → 磁碟 → 名稱，前端照順序渲染即可）。 */
export interface ManagedHost {
  id: string
  name: string
  fqdn: string | null
  public_ip: string | null
  provider: string | null
  region: string | null
  status: HostStatus
  exclusive: boolean
  exclusive_customer_id: string | null
  exclusive_customer_name: string | null
  /**
   * 這台 VM 的**歸屬**（批99-B）：`null`＝愛+1 自營、非 null＝該代理商自建的主機。
   *
   * ★ 代理商視角**永遠是自己**（後端 `/api/admin/reseller/hosts*` 一律加
   *   `reseller_id == 我` 的條件，別家與我方自營主機對他是 404）——所以代理商頁面
   *   不需要、也不該顯示這一欄；它是我方後台用來分辨「這台是誰的機器」的。
   */
  reseller_id: string | null
  /** 歸屬代理商的公司名（後端批載）；自營＝null。 */
  reseller_name: string | null
  agent_version: string | null
  /**
   * 這台跑的是哪一種 agent（批141；`agent_kinds.kind`，註冊時定下、之後不可改）。
   * 舊後端沒有這一欄——缺席視同預設種類（顯示時退回「—」即可，不要猜名字）。
   */
  agent_kind?: string | null
  /** 是否已完成 enroll（後端＝有 agent 公鑰）。 */
  agent_enrolled: boolean
  /**
   * 這台 agent 自報「裝得起哪些產品」（批99-A）。
   *
   * ★★ 批114（裁示 ㉟）：**舊 agent 的相容規則拿掉了**。在這之前 `managed_hosts.agent_products`
   *   為 NULL（agent < 0.2.11 從沒回報過）時，後端會替它回一個固定的產品鍵——那是「LS 為
   *   某個產品服務」的殘留。現在原樣回 **null＝尚未回報**：這台不能開站，UI 要指路去升 agent。
   *   所以前端**必須判 null**（`?? []` 會把「尚未回報」與「一個都裝不起」混成同一件事，
   *   差別在提示文字，比對用途則兩者等價）。
   */
  agent_products: string[] | null
  /**
   * agent **真的回報過**支援產品嗎。
   *
   * ⚠️ 批114 起這一欄與 `agent_products !== null` 同義，留著只是相容（舊回應仍會帶）。
   *   **新程式一律判 `agent_products == null`**——單一判準才不會兩邊不一致。
   */
  agent_products_reported?: boolean
  agent_last_ip: string | null
  cpu_pct: number | null
  mem_used_pct: number | null
  /** 各掛載點的**最高**水位（明細在詳情的 `metrics.mounts`）。 */
  disk_used_pct_max: number | null
  sites_count: number
  /** 人工上限；null＝只看資源。 */
  max_sites: number | null
  labels: Record<string, unknown> | null
  last_seen: string | null
  last_inventory_at: string | null
  alert_counts: AlertCounts
  created_at: string | null
}

/** VM 上的一套 CS（`host_sites`；`GET /hosts/{id}` 才帶）。 */
export interface HostSite {
  id: string
  host_id: string
  slug: string
  /**
   * 這一站是哪個產品（批99-A；`host_sites.product`）。既有列由 migration 回填成
   * `iplusone-cs`，所以**恆有值**（不是選填）。vendor SSO 票的 `product` 也讀這一欄。
   */
  product: string
  instance_id: string
  domain: string | null
  public_url: string | null
  customer_id: string | null
  customer_name: string | null
  /** provisioning|running|stopped|decommissioning|decommissioned|failed|missing */
  state: string
  /**
   * **為什麼停**（`manual`＝後台手動停站／`trial_expired`＝試用到期自動停站／`null`＝沒被
   * 刻意停）。`state` 只說「容器沒跑」，分不出「刻意停著」與「掛了」——而「刻意停著」是
   * 「改設定」的禁止條件（套用設定會 `compose up -d`，把停用中的站悄悄拉回來）。
   */
  stopped_reason: string | null
  /** provisioned＝中台開的；discovered＝盤點時撿到的既有站。 */
  origin: string
  variant: string
  update_channel: string | null
  /** host systemd updater unit＋spool 是否存在＝`update_site` 的前置條件（批B 據此擋鈕）。 */
  updater_deployed: boolean
  /** agent 盤點看到的版本。 */
  app_version: string | null
  /** CS 心跳（`deployments`）回報的版本。 */
  cs_app_version: string | null
  /** 兩來源不一致＝升級中途或盤點過期，UI 標黃。 */
  version_mismatch: boolean
  port: number | null
  pgweb_port: number | null
  /** ok|fail|unknown（fail＝agent 本機連續 3 次直打 /health 失敗）。 */
  health: string | null
  needs_setup: boolean | null
  /** none|pending_dns|issued|expiring|failed */
  tls_state: string
  cert_expires_at: string | null
  /**
   * 公開網址（批135）：`pub.<slug>.<zone>`，BYO 站也是我方 zone。null＝還沒指派
   * （舊站由中台背景自動補、盤點發現的站不指派）。網域與 port 以中台為準。
   */
  pub_domain: string | null
  pub_port: number | null
  /** none|waiting|enabling|active|failed|unsupported（只由任務結果與盤點寫）。 */
  pub_state: string
  /** 原因**代碼**（例 `bundle_unsupported`）；不含 certbot 原文。 */
  pub_detail: string | null
  license_id: string | null
  backup_latest_at: string | null
  backup_latest_bytes: number | null
  backup_status: string | null
  data_dir: string | null
  install_dir: string | null
  backup_dir: string | null
  /**
   * 這站的容器清單（agent 0.2.8+）。後端回 `containers or []`，所以**空陣列＝沒東西可展開**
   * （不必分「舊 agent 沒回報」與「真的零容器」——UI 上都是不給展開）。
   */
  containers: SiteContainer[]
  /** CS 心跳時間（來自 `deployments`，非 agent 盤點）。 */
  last_seen: string | null
  last_inventory_at: string | null
  created_at: string | null
  /**
   * 這站的產品支援哪些代管功能（批141；產品 `hosting_profile.features`）：
   * `pub_url`／`vendor_sso`／`tunnel`／`pgweb`／`site_config`／`sso_google_managed`。
   * **null＝該產品沒有有效的代管設定**——一律當成「全部不支援」（`lib/hostingProfile.ts::hasFeature`）。
   * 舊後端沒有這一欄（undefined）：同樣當成不支援會把既有畫面整片藏掉，所以呼叫端對
   * `undefined` 要退回「照舊顯示」（見 `siteFeature()`）。
   */
  hosting_features?: Record<string, boolean> | null
}

/** 告警列（**未解決狀態表、非事件流**）。 */
export interface HostAlert {
  id: string
  /**
   * `host`／`site`；批169 起另有 `deploy`（部署層：配額用量告警，自部署站台沒有主機與站台列，
   * 歸屬看 `customer_*`／`instance_*`）。中台層的待辦另有 `fleet` 這類值——一律當字串處理。
   */
  scope: string
  /** host_offline／disk_high／disk_forecast／site_offline／cert_expiring／license_expiring／
   *  needs_setup／backup_stale／site_drift／site_oom（批B 起另有 job_failed 等；批169 quota_usage）。 */
  kind: string
  severity: AlertSeverity
  host_id: string | null
  host_name: string | null
  site_id: string | null
  site_slug: string | null
  /** 批169：部署層告警的客戶（其餘 kind 恆為 null；舊版 LS 沒有這一欄）。 */
  customer_id?: string | null
  customer_name?: string | null
  /** 批169：部署層告警對應的部署（`deployments.instance_id`）與它的顯示名。 */
  instance_id?: string | null
  instance_name?: string | null
  detail: Record<string, unknown> | null
  /** 被父告警抑制（host 離線期間的子告警照寫但標記；清單預設濾掉）。 */
  suppressed: boolean
  first_seen: string | null
  last_seen: string | null
  acked_at: string | null
  acked_by: string | null
}

/** 詳情（`GET /hosts/{id}`）＝清單列＋資源快照＋站台／告警／任務。 */
/**
 * **主機設定**的一個欄位（批112）——中台交給這台 VM 上站台的環境設定值。
 *
 * 欄位集合由**設定樣板**宣告（`agent_config_templates`，我方後台維護的資料），中台不
 * 解讀語意；這裡的 `label`／`hint` 都是從那份宣告來的，所以 UI 完全是動態渲染。
 *
 * ★★ **秘密欄位（`secret: true`）的 `value` 恆為 `null`**，能顯示的只有 `masked`
 *   （頭尾遮罩）。後端從來不回明文——要「複製金鑰」這種功能就是設計錯誤。
 */
export interface AgentConfigField {
  key: string
  label: string
  type: 'string' | 'number' | 'bool'
  hint: string | null
  secret: boolean
  /** 組好的環境變數名（`<NAMESPACE>_<KEY>`，純顯示用，讓維運看得懂）。 */
  env_key: string
  deprecated: boolean
  /** 這台主機**有沒有自己設過**（false＝顯示的是樣板宣告的預設值）。 */
  configured: boolean
  /** 非秘密欄位的值；**秘密欄位恆為 null**。 */
  value: string | number | boolean | null
  /** 秘密欄位的頭尾遮罩（`AQ.…9xyz`）；非秘密欄位為 null。 */
  masked: string | null
  options?: string[]
  min?: number | null
  max?: number | null
}

/**
 * 這台主機上**一份已掛載的設定樣板**（1.0.44 起取代原本「一個產品一塊」的形狀）。
 *
 * 值依 `namespace` 分層 ⇒ 同 namespace 的兩份樣板共用同一層（後端聯集校驗）。
 */
export interface AgentConfigBlock {
  template_id: string | null
  name: string
  namespace: string
  description: string | null
  /** 下發給哪些產品的站台；`null`＝全部。 */
  products: string[] | null
  sort_order: number
  /** 範圍 ∩ 這台自報支援的產品＝實際會收到這組 env 的產品。空＝現在誰也收不到。 */
  effective_products: string[]
  fields: AgentConfigField[]
}

export interface ManagedHostDetail extends ManagedHost {
  os_info: Record<string, unknown> | null
  capacity: Record<string, unknown> | null
  /**
   * 全機 image 盤點（agent 0.2.8+）。**null＝不知道**（舊 agent 沒回報過／docker 問不到）
   * → UI 顯示「需 agent 0.2.8 以上」；`[]`＝這台確實沒有 image。
   * 清單頁（`ManagedHost`）刻意不帶這一欄——一台上百筆，清單會變幾百 KB。
   */
  images: HostImage[] | null
  metrics: HostMetrics | null
  metrics_at: string | null
  metrics_daily: HostDailyMetric[] | null
  machine_anchors: Record<string, unknown> | null
  notes: string | null
  /** 磁碟線性外推「還有幾天填滿」；null＝點數不足或沒在成長（不預警）。 */
  disk_forecast_days: number | null
  sites: HostSite[]
  alerts: HostAlert[]
  /**
   * ⚠️ 後端至今固定回空陣列（形狀先佔位）。**真的最近任務請走 `listHostJobs({ hostId })`**
   * ——主機詳情頁就是這樣拿的（批B）。
   */
  recent_jobs: HostJob[]
  /**
   * 主機設定（批112）＝**已掛載的設定樣板**清單（依 `sort_order`）。
   * **秘密欄位只有遮罩**（見 `AgentConfigField`）。空陣列＝這台還沒掛任何樣板。
   */
  agent_config: AgentConfigBlock[]
  /**
   * **主機服務**（批169）：全機共用一套的選配服務，由 agent 在全量心跳回報（ipo-agent 0.2.17+）。
   * **null＝這台 agent 從沒回報過**（版本太舊）＝派不出任何主機服務；`[]`＝回報了「一個都沒有」。
   * ★ 服務名、中文名與預設 channel 全部來自 agent——前端與 LS 都不寫服務名字面。
   */
  host_services: HostService[] | null
  /** `host_services` 最後一次被寫入的時間（agent 那一步失敗時不送、LS 保留舊值）。 */
  host_services_at: string | null
}

/** 主機服務的一筆問題（哪一站沒接上、為什麼）。`code` 的中文在 `lib/hostServices.ts`。 */
export interface HostServiceIssue {
  code: string
  site: string | null
  detail: string | null
}

/** 一個主機服務的現況（agent 回報、LS 寬鬆正規化後原封存）。 */
export interface HostService {
  /** 協定識別子（派工路徑用）。 */
  service: string
  /** agent 回報的中文名（缺＝退回 `service`）。 */
  label: string | null
  /** 派工沒指定 channel 時 agent 用的 dl channel。 */
  default_channel: string | null
  installed: boolean
  version: string | null
  /** 實際安裝時用的 channel（沒裝＝null）。 */
  channel: string | null
  /** `not_installed`／`running`／`stopped`／`unhealthy`／`unknown`（不認得的值原樣顯示）。 */
  state: string | null
  protocol: number | null
  sites_total: number | null
  sites_attached: number | null
  issues: HostServiceIssue[]
}

export type HostServiceAction = 'install' | 'upgrade' | 'remove'

export interface HostServiceIn {
  action: HostServiceAction
  /** 留空＝agent 取該 channel 最新版（remove 不收）。 */
  version?: string
  /** 留空＝服務自報的預設 channel（remove 不收）。 */
  channel?: string
  idempotency_key?: string
}

/** 一次性註冊碼＋貼上即可跑的安裝指令。**明碼只在建立／重發的那個回應裡出現一次**。 */
export interface EnrollInfo {
  code: string
  expires_in_minutes: number
  /**
   * 預設種類（ipo 系那一條）的安裝指令；舊前端只認這個鍵。預設種類不在時是 `installers` 的
   * 第一條，**一種都沒登錄＝`null`**（批146 起後端不再自己組 `/agent.sh`）。
   */
  command: string | null
  /**
   * 批141：每一種**已登錄**的 agent 各一條安裝指令（註冊碼不綁種類，同一張碼裝哪一種都行）。
   * 舊後端沒有這一欄——缺席時只顯示 `command`。
   */
  installers?: EnrollInstaller[]
}

export interface EnrollInstaller {
  /** `agent_kinds.kind`。 */
  kind: string
  label: string
  command: string
}

export interface ManagedHostIn {
  /** 我方短代號，後端 pattern `^[a-z0-9][a-z0-9-]{1,62}$`；建立後不可改。 */
  name: string
  fqdn?: string | null
  public_ip?: string | null
  provider?: string | null
  region?: string | null
  exclusive?: boolean
  exclusive_customer_id?: string | null
  /**
   * 歸屬代理商（批99-B；`null`／缺席＝我方自營）。**只有我方後台送得出這一欄**
   * ——代理商 realm 的 `POST`／`PUT` 不收它（後端強制寫成自己），送了會被忽略。
   */
  reseller_id?: string | null
  max_sites?: number | null
  labels?: Record<string, unknown> | null
  notes?: string | null
}

/** 部分更新（只送有帶的欄）；`exclusive` 改 false 時後端會一併鬆開綁定客戶。 */
export type ManagedHostPatch = Partial<Omit<ManagedHostIn, 'name'>> & { status?: HostStatus }

export interface AlertQuery {
  includeSuppressed?: boolean
  includeResolved?: boolean
  /** 1–200（後端硬上限），預設 50。 */
  limit?: number
  /** 上一頁的 `next_cursor`，原封不動帶回來（**不要解析它**，見 `HostAlertPage`）。 */
  cursor?: string | null
}

/**
 * 一頁告警（keyset 分頁，批93-D）。`next_cursor` 非 null＝還有下一頁。
 *
 * ★ 游標是**不透明字串**——別解析它。內容是後端的排序鍵（告警是 `(last_seen, id)`，
 *   不是 `created_at`：這張表是未解決狀態表，同一列會被對帳反覆 bump），
 *   哪天換了排序，解析過它的呼叫端就全壞。
 *
 * ⚠️ 嚴重度排序是**本頁內**的顯示排序（後端行為與批93-D 之前相同）——跨頁比較嚴重度
 *   沒有意義，要看全貌請把頁翻完。
 */
export interface HostAlertPage {
  items: HostAlert[]
  next_cursor: string | null
}

/** 掛載點路徑（agent 鍵名尚未定版 → 依序容錯取值）。 */
export function mountPath(m: HostMount): string {
  return m.path || m.mount || m.mountpoint || m.device || '—'
}

// --- 主機（operator 可讀；建立／修改／註冊碼＝admin 以上）---
export const listManagedHosts = (opts: { includeRetired?: boolean; resellerId?: string } = {}) =>
  api<ManagedHost[]>(
    `${base()}/hosts${qs({
      include_retired: opts.includeRetired ? 'true' : undefined,
      // 歸屬過濾（批99-B，我方視角專用）：值**只能是代理商 uuid**。
      // ⚠️ 後端刻意沒有「只看自營」的值（查詢字串表達不了 `IS NULL` 與「沒帶」的差別，
      //    見 `admin_hosting.list_hosts` 的 docstring）——那個視角由呼叫端自己濾
      //    `reseller_id === null`（表 <50 列）。送非 uuid 進來會被 FastAPI 擋成 422。
      reseller_id: opts.resellerId || undefined,
    })}`,
  )
export const getManagedHost = (id: string) => api<ManagedHostDetail>(`${base()}/hosts/${id}`)
/**
 * 建立代管主機。
 *
 * - **我方 realm**：建立即發第一張註冊碼，回應**同時**帶 `enroll`，明碼之後再也拿不到
 *   （只能重發）。
 * - **代理商 realm（批102 V4）**：回應**沒有** `enroll`，改帶一句 `enroll_hint`
 *   ——註冊碼＝「在一台 VM 上裝 agent 並掛進中台」的全部憑證，而 agent 自報的
 *   `instance_id` 會被中台自動歸戶；能自助發碼的人就能把別人的站認領進自己的代管檔。
 *   所以發碼收回我方（`api/admin_reseller_hosting.py` 的 ⚠️⚠️ 段）。
 */
export const createManagedHost = (body: ManagedHostIn) =>
  api<ManagedHost & { enroll?: EnrollInfo; enroll_hint?: string }>(`${base()}/hosts`, {
    method: 'POST',
    body,
  })
export const updateManagedHost = (id: string, body: ManagedHostPatch) =>
  api<ManagedHost>(`${base()}/hosts/${id}`, { method: 'PUT', body })
/**
 * 改這台主機的**主機設定**（批112）。一次針對一個產品送一組值。
 *
 * ★★ **秘密欄位「空值＝不改」**：我們只看得到遮罩，送回遮罩就會把真金鑰洗掉。
 *   所以 UI 對秘密欄位一律用空的輸入框（placeholder 顯示遮罩），沒輸入就不要送那個鍵。
 *   要清掉請明確送 `null`。
 *
 * ⚠️ **我方 realm 專屬**：代理商 realm 沒有這支端點（裁示 35，日後要開再說）。
 */
export const updateHostAgentConfig = (
  id: string,
  body: { namespace: string; values: Record<string, unknown> },
) =>
  api<{ agent_config: AgentConfigBlock[]; changed: string[] }>(
    `${base()}/hosts/${id}/agent-config`,
    { method: 'PUT', body },
  )

/**
 * 把一份設定樣板掛到主機上（**我方 realm 專屬**，`require_admin`）。
 *
 * ★ 撞名時後端回 409，`detail.env_names` 是撞到的環境變數名清單——UI 要把那串顯示出來，
 *   否則操作者只看到「衝突」卻不知道改哪裡。
 */
export const attachHostTemplate = (
  id: string,
  body: { template_id: string; products?: string[] | null; sort_order?: number },
) =>
  api<{ template: unknown; agent_config: AgentConfigBlock[] }>(`${base()}/hosts/${id}/templates`, {
    method: 'POST',
    body,
  })

/** 改一筆掛載的產品範圍／排序。 */
export const updateHostTemplate = (
  id: string,
  templateId: string,
  body: { products?: string[] | null; sort_order?: number },
) =>
  api<{ template: unknown; agent_config: AgentConfigBlock[] }>(
    `${base()}/hosts/${id}/templates/${templateId}`,
    { method: 'PUT', body },
  )

/**
 * 卸載一份樣板。**同 namespace 沒有其他掛載時，已存的值一起刪**
 * （回應的 `removed_values` 是被刪掉的欄位鍵）。
 */
export const detachHostTemplate = (id: string, templateId: string) =>
  api<{ removed_values: string[]; agent_config: AgentConfigBlock[] }>(
    `${base()}/hosts/${id}/templates/${templateId}`,
    { method: 'DELETE' },
  )
/**
 * 對既有站台派一支「改站台設定」的工（批112）——改該站 `.env` 的指定鍵值並重建容器。
 *
 * `values` 的鍵是**產品宣告的欄位鍵**（不是環境變數名）；秘密欄位一律 422
 * （秘密不進 job params，它走 agent realm 那條密文通道）。
 */
export const dispatchSiteConfig = (siteId: string, values: Record<string, unknown>) =>
  api<HostJob>(`${base()}/sites/${siteId}/config`, { method: 'POST', body: { values } })
/**
 * 主機服務的安裝／升級／移除（批169，`host_service` 任務，★簽章）。admin 以上。
 *
 * ⚠️ **我方 realm 專屬**（裁示 51：代理商自帶的主機也只有我方能派）：代理商 realm 沒有這支端點，
 * 呼叫端一律以 `auth.isReseller` 整塊不渲染按鈕（同 `reissueEnrollCode`）。
 * 409：`service_unsupported`（agent 沒回報這個服務）／`job_active`（同主機有進行中的任務）。
 */
export const dispatchHostService = (hostId: string, service: string, body: HostServiceIn) =>
  api<HostJob>(`${base()}/hosts/${hostId}/services/${encodeURIComponent(service)}`, {
    method: 'POST',
    body,
  })
/**
 * 重發註冊碼。⚠️ 舊 agent 金鑰**立即失效**（host 打回 pending＋清公鑰）。
 *
 * ⚠️ **我方 realm 專屬**（批102 V4）：代理商 realm 沒有這支端點，打過去是 404。
 * 呼叫端一律以 `auth.isReseller` 整塊不渲染那顆按鈕（同 `issueHostSiteSsoTicket`）。
 */
export const reissueEnrollCode = (id: string) =>
  api<EnrollInfo>(`${base()}/hosts/${id}/enroll-code`, { method: 'POST' })

// --- 換機：整批重簽授權（批80）---
//
// 代管站**沒有**逐站轉移（`ls.ts::transferHost` 對代管站後端回 403）：VM 遷移／擴容／重建
// 換掉的是主機 machine anchors，那台上每一站的授權指紋會同時對不上，CS 端 OR 比對全不等
// 就把站台轉唯讀。所以是主機層一次重簽，instance_id 與 DeploymentLink 都不動。

/** 試算列：一站一列（`matches=false` 才會被重簽）。 */
export interface RelicenseSite {
  slug: string
  instance_id: string
  license_id: string
  plan: string | null
  /**
   * 這張證的一級數量鏡像欄（**相容輸出**）。批114 起畫面改讀 `quotas`，這一欄留給
   * 舊呼叫端；新程式不要再讀它。
   */
  seats: number
  /**
   * 這張證的數量配額（批114）——試算表改成顯示配額摘要，不再印單一數字欄。
   * 選填：舊版 LS 的試算回應沒有這一欄（那時畫面會退成「—」）。
   */
  quotas?: Record<string, number | null> | null
  expires_at: string | null
  /** 這張證綁的指紋是否仍與主機當前 anchors 相符（判定式與 CS 端逐字同語意）。 */
  matches: boolean
}

/** 略過的站：`no_active_license`（沒有可重簽的證）／`already_bound`（指紋已符合）。 */
export interface RelicenseSkip {
  slug: string
  reason: string
}

/** `dry_run: true` 的試算結果。 */
export interface RelicensePreview {
  host: { id: string; name: string; status: HostStatus }
  /** 主機當前 machine anchors（鍵名才是 UI 要顯示的；值是綁機材料，只印鍵）。 */
  anchors: Record<string, string>
  sites: RelicenseSite[]
  to_resign: number
  skipped: RelicenseSkip[]
}

/** `dry_run: false` 的執行結果。 */
export interface RelicenseResult {
  resigned: { slug: string; old_license_id: string; new_license_id: string }[]
  skipped: RelicenseSkip[]
}

/**
 * 整批重簽（admin 以上）。**先用 `dryRun=true` 試算再讓人確認**。
 *
 * 主機沒有任何 anchors 時後端回 409 `host_has_no_anchors`（沒錨點就重簽＝默默拿掉綁機）。
 * 重簽後不需要派工：agent 心跳 ≤60 秒會宣告新證、寫檔並重啟該站 api。
 */
export function relicenseHost(hostId: string, dryRun: true): Promise<RelicensePreview>
export function relicenseHost(hostId: string, dryRun: false): Promise<RelicenseResult>
export function relicenseHost(
  hostId: string,
  dryRun: boolean,
): Promise<RelicensePreview | RelicenseResult> {
  return api<RelicensePreview | RelicenseResult>(`${base()}/hosts/${hostId}/relicense`, {
    method: 'POST',
    body: { dry_run: dryRun },
  })
}

// --- 告警（operator 可讀＋可 ack）---
export const listHostAlerts = (q: AlertQuery = {}) =>
  api<HostAlertPage>(
    `${base()}/alerts${qs({
      include_suppressed: q.includeSuppressed ? 'true' : undefined,
      include_resolved: q.includeResolved ? 'true' : undefined,
      limit: q.limit ? String(q.limit) : undefined,
      cursor: q.cursor || undefined,
    })}`,
  )
export const ackHostAlert = (id: string) =>
  api<HostAlert>(`${base()}/alerts/${id}/ack`, { method: 'POST' })

// ===========================================================================
// 派工（批B）——`host_jobs` 狀態機。中文標籤／階段序列／終結態判斷在 `lib/jobs.ts`。
// ===========================================================================

/**
 * job 狀態（後端 `services/jobs.py`）。終結態＝succeeded／failed／canceled／expired
 * （判斷一律用 `lib/jobs.ts` 的 `isTerminalJobState`，**進度頁靠它停止輪詢**）。
 */
export type JobState =
  | 'queued'
  | 'leased'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'expired'
  | 'canceled'

/** 一筆派工（`GET /jobs`／建立與生命週期端點的回應都是這個形狀）。 */
export interface HostJob {
  /** 12 hex 短碼；**API 路徑用的就是它**（不是 uuid 主鍵）。 */
  job_id: string
  host_id: string
  host_name: string | null
  site_id: string | null
  site_slug: string | null
  /**
   * 客戶歸屬（批80 批B4）——兩條推導路徑：站台類 job 走站台的客戶；主機層 job
   * （`site_id` 為 null）只在該台是**獨佔 VM** 時歸給獨佔客戶（整台只服務他一家）。
   * 非獨佔 VM 的主機層 job 為 null＝沒有歸屬，硬要指一家是錯的。
   * ⚠️ 主機詳情頁的 `recent_jobs` 刻意不帶（那已在客戶的脈絡裡）→ 兩欄恆 null。
   */
  customer_id: string | null
  customer_name: string | null
  /** `lib/jobs.ts` 的 `JOB_KIND_LABELS` 九種之一。 */
  kind: string
  state: JobState
  /** agent 回報的階段（updater 8 語彙／開站 12 步；未開跑＝null）。 */
  phase: string | null
  progress: number
  detail: string | null
  attempts: number
  max_attempts: number
  params: Record<string, unknown> | null
  /** 帶 IPOJOB1 指令簽章（高風險 kind：開站／停站／復站／回收）。 */
  signed: boolean
  requested_by: string | null
  result: Record<string, unknown> | null
  error: string | null
  deadline_at: string | null
  created_at: string | null
  started_at: string | null
  finished_at: string | null
}

/** 一行 job log；`seq` 兼冪等鍵，也是增量拉取的游標。 */
export interface JobLogRow {
  seq: number
  at: string | null
  level: string
  text: string
}

/** 詳情（`GET /jobs/{job_id}?since=<seq>`）：`logs` 只含 seq > since 的新行。 */
export interface HostJobDetail extends HostJob {
  logs: JobLogRow[]
  /** 下一次輪詢要帶的 `since`（本批沒有新行時＝原 since，不會倒退）。 */
  log_cursor: number
}

/** 後端單次最多回這麼多行 log；等於這個數＝可能還有下一批（別急著停輪詢）。 */
export const JOB_LOG_PAGE = 2000

export interface JobQuery {
  hostId?: string
  /** 客戶過濾（伺服器端；推導規則同 `HostJob.customer_id` 的註解）。 */
  customerId?: string
  state?: JobState
  /** 1–200（後端硬上限），預設 50。 */
  limit?: number
  /** 上一頁的 `next_cursor`，原封不動帶回來（**不要解析它**）。 */
  cursor?: string | null
}

/**
 * 一頁任務（keyset 分頁，批93-D）。`next_cursor` 非 null＝還有下一頁。
 *
 * 排序鍵是 `(created_at, id)` DESC——`id` 是 tie-breaker，不是裝飾：批次升級一次插
 * 幾十列 `created_at` 完全相同的 job，少了它翻頁會整批漏掉。
 */
export interface HostJobPage {
  items: HostJob[]
  next_cursor: string | null
}

export interface JobIn {
  kind: string
  site_id?: string | null
  params?: Record<string, unknown>
  /** 撞到既有列＝回傳既有那筆（不會產生兩個部署動作）。 */
  idempotency_key?: string | null
}

/**
 * 批次升級的結果（批192）：改成「依序更新佇列」——每台主機一個 run（`source='batch'`）。
 * `skipped` 一定要顯示原因（主機不在役／未佈更新器）。
 */
export interface BatchUpgradeResult {
  batch_id: string
  runs: { run_id: string; host_id: string; queued: number }[]
  /** 排入佇列的站台總數。 */
  queued: number
  skipped: { slug: string; reason: string }[]
}

export const listHostJobs = (q: JobQuery = {}) =>
  api<HostJobPage>(
    `${base()}/jobs${qs({
      host_id: q.hostId,
      customer_id: q.customerId,
      state: q.state,
      limit: q.limit ? String(q.limit) : undefined,
      cursor: q.cursor || undefined,
    })}`,
  )
/** `since` 帶上一輪的 `log_cursor`＝只拉新行（進度頁 2 秒輪詢靠這個才不會重送全部 log）。 */
export const getHostJob = (jobId: string, since = 0) =>
  api<HostJobDetail>(`${base()}/jobs/${jobId}${qs({ since: since ? String(since) : undefined })}`)
/** 派工。operator 只能發 update_site／run_backup／collect_diagnostics（後端二次 gate）。 */
export const createHostJob = (hostId: string, body: JobIn) =>
  api<HostJob>(`${base()}/hosts/${hostId}/jobs`, { method: 'POST', body })
/** 取消（admin 以上）：queued 直接標記；已在跑的由 agent 下次回報時中止。 */
export const cancelHostJob = (jobId: string) =>
  api<HostJob>(`${base()}/jobs/${jobId}/cancel`, { method: 'POST' })
/** 重試＝**建新列**（舊列的 attempts／log／時間戳都留著看）；回傳新 job。 */
export const retryHostJob = (jobId: string) =>
  api<HostJob>(`${base()}/jobs/${jobId}/retry`, { method: 'POST' })
/**
 * 批次升級（批192）：全部代管站依序一次一站；`include_plugins` 一併更新擴充套件。
 * 失敗的站會略過並告警，回滾也失敗才暫停。
 */
export const batchUpgradeHostSites = (siteIds: string[], version?: string | null, includePlugins = false) =>
  api<BatchUpgradeResult>(`${base()}/sites/batch-upgrade`, {
    method: 'POST',
    body: { site_ids: siteIds, version: version || undefined, include_plugins: includePlugins },
  })

// ===========================================================================
// 預約更新（批192）：固定維護時段＋單次預約＋逐站進度
// ===========================================================================

/** 固定維護時段。`weekdays` 是 ISO 星期（1＝週一）；時間以 `UpgradePlan.timezone` 解讀。 */
export interface UpgradeWindow {
  enabled: boolean
  weekdays: number[]
  /** `HH:MM`。 */
  time: string
  include_plugins: boolean
  /** 30..720，預設 180。 */
  max_minutes: number
  /** 站台依序更新的順序（site id）。 */
  site_order: string[]
  updated_at?: string
}

export type UpgradeRunItemStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'done_warn'
  | 'failed'
  | 'skipped'
  | 'skipped_deadline'
  | 'canceled'

export interface UpgradeRunItem {
  site_id: string
  slug: string
  job_id: string | null
  status: UpgradeRunItemStatus
  from: string | null
  to: string | null
  plugins: { upgraded: number; failed: number; skipped: number } | null
  detail: string | null
}

export type UpgradeRunStatus = 'scheduled' | 'running' | 'done' | 'partial' | 'paused' | 'canceled' | 'missed'

export interface UpgradeRun {
  id: string
  host_id: string
  source: 'once' | 'window' | 'batch'
  run_at: string
  deadline_at: string | null
  include_plugins: boolean
  version: string | null
  status: UpgradeRunStatus
  batch_id: string | null
  created_by: { id: string; email: string } | null
  created_at: string
  started_at: string | null
  finished_at: string | null
  items: UpgradeRunItem[]
  /** 在全域佇列中第幾個；只有 scheduled／running 有。 */
  position: number | null
}

export interface UpgradePlan {
  window: UpgradeWindow | null
  next_window_at: string | null
  /** 後端解讀維護時段與預約時間的時區（例：Asia/Taipei）。 */
  timezone: string
  /** false＝主機 agent 版本不足（需 `agent_min_version` 以上）。 */
  agent_supported: boolean
  agent_min_version: string | null
  /** false＝這個產品／agent 不支援順帶更新擴充套件。 */
  plugins_supported: boolean
  /** 未結束的全部＋最近 10 筆已結束。 */
  runs: UpgradeRun[]
}

export interface UpgradeRunIn {
  /** ISO 時間，需 ≥ 現在＋5 分鐘、≤ 30 天。 */
  run_at: string
  /** 有順序。 */
  site_ids: string[]
  include_plugins: boolean
  max_minutes: number
}

export const getUpgradePlan = (hostId: string) =>
  api<UpgradePlan>(`${base()}/hosts/${hostId}/upgrade-plan`)
/** 設定固定維護時段（`enabled=false` 視同關閉但保留設定）。 */
export const setUpgradeWindow = (hostId: string, body: Omit<UpgradeWindow, 'updated_at'>) =>
  api<UpgradeWindow>(`${base()}/hosts/${hostId}/upgrade-window`, { method: 'PUT', body })
/** 建立單次預約。409 `schedule_exists`｜`agent_too_old`。 */
export const createUpgradeRun = (hostId: string, body: UpgradeRunIn) =>
  api<UpgradeRun>(`${base()}/hosts/${hostId}/upgrade-runs`, { method: 'POST', body })
/** 取消：scheduled 直接取消；running／paused 停止派送後面的站（正在跑的那站跑完）。 */
export const cancelUpgradeRun = (hostId: string, runId: string) =>
  api<UpgradeRun>(`${base()}/hosts/${hostId}/upgrade-runs/${runId}`, { method: 'DELETE' })

// ===========================================================================
// 開站（批C2）＋站台生命週期（批D）
// ===========================================================================

/** 開站精靈送出的完整 body（preflight 與 provision 共用同一形狀）。 */
export interface ProvisionIn {
  customer_id: string
  /**
   * 要開哪個產品的站（批99-A）。**選填**：缺席＝後端的 `settings.default_product_key`
   * （既有呼叫端不改也不會 422）。不過三道閘照樣要過——產品要 `supports_hosting`、
   * 要在這台 agent 的 `agent_products` 裡、還要在自己的產品範圖內。
   */
  product?: string
  /** 2–24 字小寫英數與連字號；dev 環境後端會自動補 `-dev` 後綴（回應的 `normalized_slug`）。 */
  slug: string
  term_days?: number
  /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes?: number
  modules?: Record<string, string> | null
  /**
   * 數量配額（值 `null`＝無上限）。
   * ★ 批114：開站精靈的獨立數量欄已拔除，一律只送這一包。後端仍留著一級相容欄給舊
   *   呼叫端（`services/quota_compat.py`），但**預設值已改成不指定**——精靈不送就是
   *   由 `quotas`／套裝模板決定，不會再靜默變成某個固定數字。
   */
  quotas?: Record<string, number | null> | null
  plan?: string | null
  /**
   * 批112 修正：這張證是不是**試用**（到期自動停站、可用原廠共用 AI 額度的判準）。
   * 選 `kind === 'trial'` 的套裝時後端會自己推導，這個旗標給「客製內容也要當試用」用；兩者 OR。
   */
  trial?: boolean
  /** ours＝我方子網域自動建 A 記錄；byo＝客戶自帶網域（要客戶自己指過來）。 */
  domain_mode?: 'ours' | 'byo'
  byo_domain?: string | null
  tunnel_enabled?: boolean
  pgweb_enabled?: boolean
  /**
   * 批151 C6：開站時一併設定的**站台機密** `{欄位鍵: 值}`（欄位由產品代管設定的 `site_secrets`
   * 宣告）。**只有我方營運主管**帶得了（API 金鑰 403、低於營運主管 403、代理商 realm 422），
   * 而且**只在最後送出時帶**——preflight 不帶（試算會比對「這把值是不是已被別站使用」，那等於反查）。
   */
  site_secrets?: Record<string, string> | null
}

/** 開站前試算（不寫任何資料）——第三步的 slug 即時檢查與第五步的確認總表共用。 */
/**
 * 網域檢查結果——**兩種模式看的東西完全不同**：
 *
 * - `ours`：網域是我們產的（`<slug>.<zone>`），可用性已由 slug 三層防護把關；
 *   這裡只補「會建成什麼、A 記錄會指到哪」。
 * - `byo`：網域是客戶的，重點是**客戶指過來了沒**。`points_here=false` 不是錯誤
 *   （開站會先跳過 TLS，之後用「補簽憑證」），但 `in_use` 或 `format_ok=false` 是阻斷級。
 */
export interface DomainCheck {
  mode: 'ours' | 'byo'
  domain: string | null
  /** 我方會不會建 A 記錄（BYO 恆 false）。 */
  will_create_dns: boolean
  /** 該指到的主機 IP。 */
  target_ip: string | null
  /** 給人看的一句話（後端已寫好文案，UI 直接顯示）。 */
  note: string
  // --- 只有 BYO 有 ---
  format_ok?: boolean
  in_use?: boolean
  /** 目前實際解析到的 A 記錄（空＝還查不到）。 */
  resolved_ips?: string[]
  points_here?: boolean
}

export interface ProvisionPreflight {
  /** false＝`issues` 非空，後端 provision 一定會擋。 */
  ok: boolean
  issues: string[]
  /** 正規化後的 slug（dev 會多 `-dev`）。 */
  normalized_slug: string
  /** 只有 slug 那一層的錯（已登記／zone 已有記錄／格式）。 */
  slug_error: string | null
  /**
   * 撞到保留字的**提示**（`www-cs`／`acme-mail` 之類）。
   * 我方後台不拿黑名單擋人（那只對 portal 的客戶自助強制），所以這是提醒不是錯誤——
   * 不影響 `ok`，也不在 `issues` 裡。
   */
  slug_note: string | null
  /**
   * 最終網域。**自帶網域而客戶網域還沒填時是 `null`**——不會退回
   * `<slug>.<zone>`（那是我們根本不會建立的名字）。
   */
  domain: string | null
  domain_mode: 'ours' | 'byo'
  /** 依模式而異的網域檢查結果（UI 直接顯示 `note`）。 */
  domain_check: DomainCheck
  host: {
    name: string
    public_ip: string | null
    sites_count: number
    max_sites: number | null
    /** agent 心跳快照算出來的餘裕；null＝尚未回報指標。 */
    mem_free_mb: number | null
    disk_free_gb: number | null
  }
  le_weekly: { used: number; budget: number }
  /** clouddns／cloudflare／manual。 */
  dns_provider: string
  /**
   * 批141：這個產品的站台落在哪個代管 zone（`<slug>.<zone>`）。**別在前端寫死網域**——
   * zone 依產品的代管設定而異。舊後端沒有這一欄。
   */
  zone?: string
  /** 這次預檢針對的產品（`products.key`）。 */
  product?: string
  /**
   * 會裝哪個出貨 channel 的包。**ls-dev 開的站吃測試那一條**（測試站要測得到還沒上正式的包）
   * ——與 slug 的 `-dev` 後綴是兩件不相干的事，精靈要顯示出來免得誤會。
   * 批141：channel 依產品的代管設定；**產品沒有有效的代管設定時為 null**（此時 `issues` 必不為空）。
   */
  channel: string | null
  profile: {
    /** 每站的容器記憶體上限（docker 格式字串，如 `1g`）；產品沒有代管設定時為 null。 */
    mem: Record<string, string> | null
    /** 批141：產品的代管功能旗標（精靈據此隱藏 pgweb／反向隧道）；沒有代管設定時為 null。 */
    features?: Record<string, boolean> | null
  }
  /** 批141：不擋開站、但操作者該知道的調整（例：產品不支援反向隧道、已略過）。 */
  notes?: string[]
  /**
   * 批151 C6：產品代管設定宣告的站台機密欄位（**只在產品有宣告時出現**；值永遠不在回應裡）。
   * 以下三鍵同進退。
   */
  site_secret_fields?: { key: string; env: string; label: string; hint: string | null }[]
  /** 這台主機開站時帶得了站台機密（我方自營主機＋agent 支援）。 */
  site_secrets_supported?: boolean
  /** 授權中心有沒有設定站台機密的加密主鑰。 */
  secret_key_available?: boolean
}

export interface ProvisionResult {
  site: HostSite
  job: HostJob
  license_id: string
  /** provider 回報；`applied=false` 時 `detail` 說明要人工建什麼記錄。 */
  dns: { applied?: boolean; provider?: string; detail?: string; [key: string]: unknown }
  /**
   * 交付給客戶的初始設定網址（D10：**我方不代建帳號**）。批141：路徑依產品代管設定的
   * `setup_path`；樣板含 `{setup_token}` 的產品，網址帶著一次性權杖、**只出現在這一次回應**。
   */
  setup_url: string | null
  /**
   * 批141 M 包：`setup_url` 帶著一次性權杖嗎（產品的 `setup_path` 含 `{setup_token}`）。
   * **只信這一鍵**——代理商視角讀不到產品的代管設定，前端不自己猜網址長相。
   */
  setup_url_one_time: boolean
  /** 批141：不擋開站的調整說明（只在有內容時出現）。 */
  notes?: string[]
}

/**
 * 批次升級混搭（批141 裁示 11）：409 `mixed_batch` 的 `detail`。一批只能同產品、同出貨
 * channel——`groups` 每組是一次該按的批次。
 */
export interface MixedBatchGroup {
  product: string
  update_channel: string | null
  sites: string[]
}

/** vendor SSO 票（120 秒、一次性、綁 instance）。 */
export interface SsoTicket {
  ticket: string
  /** 直接開這個網址即進站（票在 query string 裡）。 */
  url: string
  jti: string
  expires_at: string
}

/** vendor SSO 稽核列——站上查不到，只有中台有（票 payload 刻意不含員工 email）。 */
export interface SsoGrant {
  jti: string
  issued_by: string | null
  issued_at: string | null
  expires_at: string | null
  used_at: string | null
  used_ip: string | null
}

export const preflightHostSite = (hostId: string, body: ProvisionIn) =>
  api<ProvisionPreflight>(`${base()}/hosts/${hostId}/sites/preflight`, { method: 'POST', body })
export const provisionHostSite = (hostId: string, body: ProvisionIn) =>
  api<ProvisionResult>(`${base()}/hosts/${hostId}/sites`, { method: 'POST', body })

/** 停站＝compose stop＋503 維護頁；**不撤授權**（撤了復站要重發證）。 */
export const stopHostSite = (siteId: string) =>
  api<HostJob>(`${base()}/sites/${siteId}/stop`, { method: 'POST' })
export const resumeHostSite = (siteId: string) =>
  api<HostJob>(`${base()}/sites/${siteId}/resume`, { method: 'POST' })
/** 回收（不可逆）：superadmin ＋ `confirm_domain` 必須一字不差等於站台網域。 */
export const decommissionHostSite = (
  siteId: string,
  body: { confirm_domain: string; keep_backup_days?: number | null },
) => api<HostJob>(`${base()}/sites/${siteId}/decommission`, { method: 'POST', body })
export const issueHostSiteCert = (siteId: string) =>
  api<HostJob>(`${base()}/sites/${siteId}/issue-cert`, { method: 'POST' })
export const issueHostSiteSsoTicket = (siteId: string) =>
  api<SsoTicket>(`${base()}/sites/${siteId}/sso-ticket`, { method: 'POST' })
export const listHostSiteSsoGrants = (siteId: string) =>
  api<SsoGrant[]>(`${base()}/sites/${siteId}/sso-grants`)

// ===========================================================================
// 代管開站申請的核可／婉拒（批99-B）
//
// ★★ 這三支**刻意放在本檔**而不是 `ls.ts`：它們是「代管」這條線的動作，兩個 realm
//   都有（路徑只差前綴），走 `base()` 就自動分流。放在 `ls.ts` 的話代理商視角會打到
//   `/api/admin/...` 而整片 403——那正是 `HostedApproveModal` 重用兩個 realm 的關鍵。
//
// ⚠️ 我方視角與 `ls.ts::trialPreset`／`approveHostedRequest`／`rejectLicenseRequest`
//   打的是**同一組端點**（`base()` 回 `/api/admin`）。`ls.ts` 那三支仍在——`RequestsView`
//   的一般核發流程在用，不動它們。
// ===========================================================================

/**
 * 核可代管開站的回應——與 `ls.ts::approveHostedRequest` 的行內型別**逐欄相同**
 * （那支是匿名的，這裡具名一份讓兩個 realm 共用同一個形狀）。
 *
 * ⚠️ 刻意**不含** `setup_token`：它隨 job 直接送到那台 VM，中台不再吐第二份。
 */
export type ApproveHostedResult = {
  status: string
  request_id: string
  instance_id: string
  license_id: string
  site: { slug: string; domain: string | null; public_url: string | null; [k: string]: unknown }
  job: { job_id: string; kind: string; state: string; [k: string]: unknown }
  dns: Record<string, unknown>
  setup_url: string | null
  /** 批141 M 包：`setup_url` 帶一次性權杖（只顯示這一次）。兩個 realm 都由後端判斷。 */
  setup_url_one_time: boolean
} & UnknownModulesWarning

/** 代管試用的授權初值（核可開站對話框的預設帶入）。 */
export const hostedTrialPreset = (productKey: string) =>
  api<TrialPreset>(`${base()}/products/${encodeURIComponent(productKey)}/trial-preset`)

/** 核可一筆代管開站申請 → 當場開站（回應形狀見 `ls.ts::approveHostedRequest`）。 */
export const approveHostedRequest = (id: string, input: ApproveHostedInput) =>
  api<ApproveHostedResult>(`${base()}/license-requests/${id}/approve-hosted`, {
    method: 'POST',
    body: input,
  })

/** 婉拒一筆申請（代理商只婉拒得了自家客戶的代管申請；理由客戶看得到）。 */
export const rejectHostedRequest = (id: string, body: { reason?: string | null } = {}) =>
  api<{ status: string }>(`${base()}/license-requests/${id}/reject`, { method: 'POST', body })

// --- 資源曲線（批D）---

export interface MetricsPoint {
  at: string
  cpu_pct: number | null
  mem_used_pct: number | null
  disk_used_pct_max: number | null
}

export interface MetricsSeries {
  host_id: string
  hours: number
  /** 取樣間隔（秒）；用來判斷「資料有沒有斷點」。 */
  interval_seconds: number
  points: MetricsPoint[]
}

/** 資源曲線；`hours` 1–336（後端上限實際切在 14 天）。 */
export const getManagedHostMetrics = (hostId: string, hours = 24) =>
  api<MetricsSeries>(`${base()}/hosts/${hostId}/metrics${qs({ hours: String(hours) })}`)
